import type { AtlasManifest } from '@atlas-manifest/types';
import type { SandboxOp } from './sandboxOps.js';
import { entityById, surfaceById, cloudFunctionById, eventTypeById, pathByPath } from './selectors.js';

export type OpKind = SandboxOp['kind'];

export interface OpDraft {
  kind: OpKind;
  entityId: string;
  fieldName: string;
  from: string;
  to: string;
  newType: string;
  /** add-field only — the new field's properties. */
  addType: string;
  optional: boolean;
  /** create-entity: new entity id. create-domain: new domain id. */
  newId: string;
  /** create-entity: entity display name. create-domain: domain display name. */
  newName: string;
  /** create-entity, create-surface, create-cloud-function, create-state-machine — the domain it belongs to. */
  newDomain: string;
  /** create-domain only — the domain's color token. */
  newColor: string;
  /** create-entity, create-domain, create-tag — the description. */
  newDescription: string;
  /** create-surface only. */
  newSurfaceType: string;
  /** create-surface only — owning hub id. */
  newHub: string;
  /** create-cloud-function only. */
  newFunctionKind: string;
  /** create-event only — category label. */
  newCategory: string;
  /** create-event only — 0-3, held as a string for the <select>. */
  newSafetyTier: string;
  /** create-path only. */
  newPath: string;
  /** create-path only. */
  newRetention: string;
  /** create-tag only — the tag string. */
  newTag: string;
  /** create-reference only — target entity id. */
  newFromEntity: string;
  /** create-reference only — target entity id. */
  newToEntity: string;
  /** create-reference only — the field on the source entity. */
  newFromField: string;
  /** create-reference only. */
  newReferenceKind: string;
  /** update-surface, update-cloud-function, update-event, update-path — the selector for the object being patched. */
  updateSelector: string;
  /** add-payload-field, remove-payload-field, rename-payload-field, change-payload-field-type —
   *  which array (Surface.payloadFields or EventType.payloadFields) updateSelector's owner id targets. */
  payloadFieldOwnerKind: 'surface' | 'event' | '';
  /** update-entity, update-cloud-function, update-event — patched description/free-text field. */
  updateDescription: string;
  /** update-entity, update-surface, update-cloud-function — patched domain. */
  updateDomain: string;
  /** update-entity only — patched name. */
  updateName: string;
  /** update-surface only. */
  updateSurfaceType: string;
  /** update-surface only. */
  updateHub: string;
  /** update-cloud-function only. */
  updateFunctionKind: string;
  /** update-cloud-function only. */
  updateRationale: string;
  /** update-cloud-function only. */
  updateTriggerCondition: string;
  /** update-event only. */
  updateCategory: string;
  /** update-event only. */
  updateBusinessTrigger: string;
  /** update-event only — held as a string for the <input type="number">. */
  updateRetentionDays: string;
  /** update-path only. */
  updateRetention: string;
  /** update-path only. */
  updateSecurityRuleNote: string;
  /** update-path only. */
  updateOwner: string;
  /** update-path only. */
  updateQuotaNotes: string;
  /** add-function-entity-ref, remove-function-entity-ref — which array on the CloudFunction named by updateSelector. */
  refArrayName: 'reads' | 'writes' | '';
  /** add-function-entity-ref, remove-function-entity-ref, add-event-carried-entity, remove-event-carried-entity —
   *  the entity id being added to or removed from the array/list. */
  refEntityId: string;
}

export const EMPTY_DRAFT: OpDraft = {
  kind: 'add-field',
  entityId: '',
  fieldName: '',
  from: '',
  to: '',
  newType: '',
  addType: '',
  optional: false,
  newId: '',
  newName: '',
  newDomain: '',
  newColor: '',
  newDescription: '',
  newSurfaceType: '',
  newHub: '',
  newFunctionKind: '',
  newCategory: '',
  newSafetyTier: '',
  newPath: '',
  newRetention: '',
  newTag: '',
  newFromEntity: '',
  newToEntity: '',
  newFromField: '',
  newReferenceKind: '',
  updateSelector: '',
  updateDescription: '',
  updateDomain: '',
  updateName: '',
  updateSurfaceType: '',
  updateHub: '',
  updateFunctionKind: '',
  updateRationale: '',
  updateTriggerCondition: '',
  updateCategory: '',
  updateBusinessTrigger: '',
  updateRetentionDays: '',
  updateRetention: '',
  updateSecurityRuleNote: '',
  updateOwner: '',
  updateQuotaNotes: '',
  payloadFieldOwnerKind: '',
  refArrayName: '',
  refEntityId: '',
};

export type EditableKind = 'entity' | 'surface' | 'cloud-function' | 'event-type' | 'firestore-path';

/** Looks up the object of the given kind+id in the manifest and returns a
 *  fully-populated OpDraft for editing it (kind set to the matching
 *  update-* / entity-field op, every known field prefilled). Returns
 *  EMPTY_DRAFT with a create-* kind and the id seeded in when no such
 *  object exists yet — same fallback the current seedObject logic uses. */
export function buildDraftForObject(kind: EditableKind, id: string, manifest: AtlasManifest): OpDraft {
  switch (kind) {
    case 'entity': {
      const existing = entityById(manifest, id);
      return existing
        ? { ...EMPTY_DRAFT, kind: 'update-entity', entityId: existing.id, updateName: existing.name, updateDomain: existing.domain, updateDescription: existing.description }
        : { ...EMPTY_DRAFT, kind: 'create-entity', newId: id };
    }
    case 'surface': {
      const existing = surfaceById(manifest, id);
      return existing
        ? {
            ...EMPTY_DRAFT,
            kind: 'update-surface',
            updateSelector: existing.id,
            updateSurfaceType: existing.type,
            updateDomain: existing.domain,
            updateHub: existing.hub,
            updateDescription: existing.description ?? '',
          }
        : { ...EMPTY_DRAFT, kind: 'create-surface', newId: id };
    }
    case 'cloud-function': {
      const existing = cloudFunctionById(manifest, id);
      return existing
        ? {
            ...EMPTY_DRAFT,
            kind: 'update-cloud-function',
            updateSelector: existing.name,
            updateFunctionKind: existing.kind,
            updateDomain: existing.domain,
            updateDescription: existing.description ?? '',
            updateRationale: existing.rationale ?? '',
            updateTriggerCondition: existing.triggerCondition ?? '',
          }
        : { ...EMPTY_DRAFT, kind: 'create-cloud-function', newId: id };
    }
    case 'event-type': {
      const existing = eventTypeById(manifest, id);
      return existing
        ? {
            ...EMPTY_DRAFT,
            kind: 'update-event',
            updateSelector: existing.type,
            updateCategory: existing.category,
            updateDescription: existing.description ?? '',
            updateBusinessTrigger: existing.businessTrigger ?? '',
            updateRetentionDays: existing.retentionDays !== undefined ? String(existing.retentionDays) : '',
          }
        : { ...EMPTY_DRAFT, kind: 'create-event', newId: id };
    }
    case 'firestore-path': {
      const existing = pathByPath(manifest, id);
      return existing
        ? {
            ...EMPTY_DRAFT,
            kind: 'update-path',
            updateSelector: existing.path,
            updateRetention: existing.retention,
            updateSecurityRuleNote: existing.securityRuleNote ?? '',
            updateOwner: existing.owner ?? '',
            updateQuotaNotes: existing.quotaNotes ?? '',
          }
        : { ...EMPTY_DRAFT, kind: 'create-path', newPath: id };
    }
  }
}
